import { useEffect, type ReactNode } from 'react';
import { AuthProvider as OidcAuthProvider, useAuth as useOidcAuth } from 'react-oidc-context';
import { oidcConfig } from '@/auth/oidcConfig';
import { setAccessToken, setIdToken } from '@/auth/tokenStore';
import { AUTH_MODE } from '@/auth/authMode';
import { LocalAuthProvider } from '@/auth/LocalAuthProvider';

/**
 * Keeps the module-level token store in sync with the OIDC session so the
 * non-React API client can attach the current tokens to requests.
 */
function TokenSync(): null {
  const auth = useOidcAuth();
  const token = auth.user?.access_token ?? null;
  const idToken = auth.user?.id_token ?? null;

  useEffect(() => {
    setAccessToken(token);
    setIdToken(idToken);
  }, [token, idToken]);

  return null;
}

/**
 * Thin wrapper around react-oidc-context's provider. Reads all configuration
 * from Vite env vars (see oidcConfig) and never hardcodes secrets.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  // Dev bypass: do not mount any provider (no Cognito to talk to).
  if (AUTH_MODE === 'dev') {
    return <>{children}</>;
  }
  // Local credential mode: hold the token in a small React context.
  if (AUTH_MODE === 'local') {
    return <LocalAuthProvider>{children}</LocalAuthProvider>;
  }
  return (
    <OidcAuthProvider {...oidcConfig}>
      <TokenSync />
      {children}
    </OidcAuthProvider>
  );
}
